/**
 * dueDate — urgency interpretation of a full UTC ISO `due_date` instant
 * (e.g. "2026-06-20T23:59:00.000Z").
 *
 * `isPast` reflects the exact instant, not just the calendar day: a test due
 * at 9am today is already past due by 3pm today, even though "today" hasn't
 * ended. `days`/`label` stay calendar-day granularity for the "N days
 * remaining" / "Due today" copy (nobody wants "2.6 days remaining"), but are
 * only meaningful once `isPast` is false — a same-day-but-already-passed due
 * date reports `days: 0` yet `label: "Past due"`.
 */
import { formatLocalDateTime } from "./dateFormat";

const DAY_MS = 86_400_000;

export interface DueDateInfo {
  /** Whole calendar days until the due date; 0 = today, negative = a prior day. */
  days: number;
  /** Status label — never includes the date itself. */
  label: string;
  /** True once the exact due instant has passed — independent of `days`. */
  isPast: boolean;
  /** Localized display, e.g. "June 24, 11:00 AM". */
  formatted: string;
  /** Tailwind text-colour class: green > 2 days, amber 0–2, red past. */
  colorClass: string;
}

/**
 * Parse a due_date UTC ISO instant into display + urgency info, or `null`
 * when the value is absent or unparseable.
 */
export function getDueDateInfo(due?: string | null): DueDateInfo | null {
  if (typeof due !== "string") return null;

  const date = new Date(due);
  if (Number.isNaN(date.getTime())) return null;

  const now = new Date();
  const isPast = now.getTime() > date.getTime();

  const dueLocalMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayLocalMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((dueLocalMidnight.getTime() - todayLocalMidnight.getTime()) / DAY_MS);

  const diffMs = date.getTime() - now.getTime();
  const totalHours = Math.floor(diffMs / 3_600_000);
  const realDays = Math.floor(totalHours / 24);
  const remainderHours = totalHours % 24;

  let label: string;
  if (isPast) {
    label = "Past due";
  } else if (realDays === 0) {
    label = totalHours > 0 ? `${totalHours}h remaining` : "Due now";
  } else {
    const hoursPart = remainderHours > 0 ? ` ${remainderHours}h` : "";
    label = `${realDays} day${realDays === 1 ? "" : "s"}${hoursPart} remaining`;
  }

  const colorClass = isPast
    ? "text-brand-error"
    : days > 2
      ? "text-brand-success"
      : "text-amber-700";

  return { days, label, isPast, formatted: formatLocalDateTime(due), colorClass };
}
